import { 
  API_CONFIG, 
  buildWCUrl, 
  getDefaultHeaders,
  type APIResponse
} from '../../config/api.enhanced';
import { useAuthStore } from '../../store/authStore';
import authService from './auth.service';

const IS_DEV = import.meta.env.DEV;

// Cache session validation to avoid extra calls on every request
let lastSessionCheckToken: string | null = null;
let lastSessionCheckAt = 0;
const SESSION_CHECK_TTL_MS = 60 * 1000; // 1 minute

// Types for booking
export interface BookingHold {
  hold_id: string;
  expires_at: string;
  expires_in: number;
  passengers: {
    adults: number;
    children: number;
    infants: number;
    total: number;
  };
  capacity_remaining: number;
}

export interface PassengerDetail {
  name: string;
  age: number;
  birth_date?: string; // Optional: ISO YYYY-MM-DD (Gregorian)
  identity_number?: string;
  identity_type?: 'national_id' | 'border_number' | 'passport' | 'other';
  passenger_type: 'adult' | 'child' | 'infant';
  phone?: string;
}

export interface BookingOrderResponse {
  order_id: number;
  order_number: string;
  total: string;
  payment_url?: string; // Optional: only for online payments like Tap
  ui_mode?: 'redirect' | 'popup'; // Optional: Tap UI mode
  public_key?: string; // Optional: Tap public key for SDK
  token?: string; // Optional: One-time security token for success page
  order_status: string;
  order_status_name?: string; // ✅ Custom translated status label from backend
  booking_details: {
    trip_title: string;
    date: string;
    time: string;
    passengers: {
      adults: number;
      children: number;
      infants: number;
      total: number;
    };
  };
}

export interface PriceCalculation {
  subtotal: number;
  discount: number; // alias for backward compatibility, use discount_amount on backend
  addons_total: number;
  // Tax related (new)
  tax_total?: number; // total tax amount (sum of breakdown)
  tax_breakdown?: Record<string, number>; // rate_id => amount
  total_excl_tax?: number; // total before tax (after discounts + addons)
  total_incl_tax?: number; // total including tax (preferred grand total)
  total: number; // kept for backward compatibility (now equals total_incl_tax if tax applies)
  currency: string;
  breakdown: {
    adults: { count: number; price: number; total: number };
    children: { count: number; price: number; total: number };
    infants: { count: number; price: number; total: number };
    addons: Array<{
      id: number;
      name: string;
      quantity: number;
      price: number;
      total: number;
    }>;
  };
}

export interface CouponResponse {
  coupon_code: string;
  discount_type: string;
  discount_amount: number;
  new_total: number;
  savings: number;
  coupon_description: string;
  expiry_date?: string;
}

export interface CreateBookingOrderData {
  productId: number;
  date: string;
  time: string;
  adults: number;
  children: number;
  infants: number;
  addons: { id: number; qty: number }[];
  passengers: PassengerDetail[];
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  holdId?: string;
  couponCode?: string;
  paymentMethod?: string;
  specialRequests?: string;
}

/**
 * Booking Service
 * Handles all booking-related API calls
 */
class BookingService {
  private language: string = API_CONFIG.DEFAULT_LANGUAGE;
  private statusLabels: Record<string, string> = {};
  private statusLabelsLocale: string | null = null;
  private statusLabelsPromise: Promise<void> | null = null;

  setLanguage(lang: string): void {
    if (API_CONFIG.SUPPORTED_LANGUAGES.includes(lang)) {
      this.language = lang;
    }
  }

  /**
   * Request method for custom endpoints that need JWT authentication
   * Used for: booking holds, pricing, creating orders
   */
  private async request<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const headers: any = {
        ...getDefaultHeaders(),
        ...options.headers,
      };

      const isExplicitAuthInvalid = (resp: any): boolean => {
        const code = String(resp?.code ?? resp?.data?.code ?? resp?.data?.data?.code ?? '');
        const message = String(resp?.message ?? resp?.data?.message ?? '');
        if (code === 'jwt_auth_invalid_token') return true;
        if (code === 'user_not_found') return true;
        if (/token\s+was\s+revoked\s+or\s+replaced/i.test(message)) return true;
        if (/token\s+expired/i.test(message)) return true;
        if (/user\s+not\s+found/i.test(message)) return true;
        if (/jwt/i.test(code)) return true;
        if (/انتهت\s+الجلسة|تم\s+تسجيل\s+الدخول\s+من\s+جهاز\s+آخر/i.test(message)) return true;
        if (/المستخدم\s+غير\s+موجود/i.test(message)) return true;
        return false;
      };

      // Add JWT token in Authorization header if user is authenticated
      const token = useAuthStore.getState().token;
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // If we have a token, validate it with the server before making booking-critical calls.
      // This prevents creating orders while the UI still thinks the user is logged in but the token is revoked/replaced.
      if (token) {
        const now = Date.now();
        const shouldCheck = token !== lastSessionCheckToken || (now - lastSessionCheckAt) > SESSION_CHECK_TTL_MS;
        if (shouldCheck) {
          const me = await authService.getUserInfo().catch((e) => ({ success: false, message: e?.message } as any));
          if (!me?.success) {
            // Only logout if the backend explicitly indicates auth invalidation.
            if (isExplicitAuthInvalid(me)) {
              useAuthStore.getState().logout();
              return {
                success: false,
                message: 'انتهت الجلسة أو تم تسجيل الدخول من جهاز آخر. الرجاء تسجيل الدخول مجدداً.'
              };
            }

            // Transient failure (network/500/etc): block booking call, but keep session.
            return {
              success: false,
              message: me?.message || 'تعذر التحقق من الجلسة حالياً. الرجاء المحاولة مرة أخرى.'
            };
          }

          // Mark session as recently verified
          lastSessionCheckToken = token;
          lastSessionCheckAt = now;

          // Optionally refresh stored user data
          const state: any = useAuthStore.getState();
          if (me?.data && typeof state.setUser === 'function') {
            state.setUser(me.data);
          }
        }
      }

  const response = await fetch(url, {
        ...options,
        headers,
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (IS_DEV) {
          console.error('Non-JSON response from booking API:', {
            url,
            status: response.status,
            contentType
          });
        }
        return {
          success: false,
          message: `API Error: Expected JSON but received ${contentType || 'unknown'}. Status: ${response.status}`
        };
      }

      const tryParseJson = async (): Promise<any> => {
        try {
          return await response.json();
        } catch {
          return {};
        }
      };

      const isAuthTokenError = (status: number, payload: any): boolean => {
        if (!(status === 401 || status === 403)) {
          return false;
        }
        const code = (payload?.code ?? payload?.data?.code ?? '') as string;
        const message = (payload?.message ?? payload?.data?.message ?? '') as string;
        return (
          code === 'jwt_auth_invalid_token' ||
          /token\s+was\s+revoked\s+or\s+replaced/i.test(message) ||
          /token\s+expired/i.test(message) ||
          /jwt/i.test(code)
        );
      };

      const attemptRefreshAndRetry = async (errorPayload: any): Promise<APIResponse<T> | null> => {
        // Only attempt if we had a token to begin with
        const currentToken = useAuthStore.getState().token;
        if (!currentToken) {
          return null;
        }

        const refreshed = await authService.refreshToken().catch(() => ({ success: false } as any));
        const newToken = (refreshed as any)?.data?.token as string | undefined;
        if (!refreshed?.success || !newToken) {
          // Force logout if refresh failed
          useAuthStore.getState().logout();
          return {
            success: false,
            message: 'انتهت الجلسة أو تم تسجيل الدخول من جهاز آخر. الرجاء تسجيل الدخول مجدداً.'
          };
        }

        // Ensure Zustand token is updated (this service reads from store)
        const state: any = useAuthStore.getState();
        if (typeof state.setToken === 'function') {
          state.setToken(newToken);
        } else {
          // Fallback
          localStorage.setItem('auth_token', newToken);
        }

        const retryHeaders: any = {
          ...getDefaultHeaders(),
          ...options.headers,
          Authorization: `Bearer ${newToken}`,
        };

        const retryResponse = await fetch(url, {
          ...options,
          headers: retryHeaders,
        });

        // If retry still fails, logout to avoid broken state
        if (!retryResponse.ok) {
          const retryPayload = await retryResponse.json().catch(() => ({}));
          if (isAuthTokenError(retryResponse.status, retryPayload)) {
            useAuthStore.getState().logout();
            return {
              success: false,
              message: 'انتهت الجلسة أو تم تسجيل الدخول من جهاز آخر. الرجاء تسجيل الدخول مجدداً.',
              data: retryPayload
            };
          }

          return {
            success: false,
            message: retryPayload.message || retryPayload.error || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
            data: retryPayload
          };
        }

        const retryData = await retryResponse.json();
        return {
          success: true,
          data: retryData,
          message: retryData.message,
          language: this.language,
          timestamp: new Date().toISOString()
        };
      };

      if (!response.ok) {
        const errorData = await tryParseJson();

        if (isAuthTokenError(response.status, errorData)) {
          const retried = await attemptRefreshAndRetry(errorData);
          if (retried) {
            return retried;
          }
        }
        
        if (IS_DEV) {
          console.error('Booking API Error:', {
            url,
            status: response.status,
            statusText: response.statusText,
            errorData,
            method: options.method || 'GET'
          });
        }
        
        return {
          success: false,
          message: errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          data: errorData
        };
      }

      const data = await response.json();

return {
        success: true,
        data,
        message: data.message,
        language: this.language,
        timestamp: new Date().toISOString()
      };

    } catch (error) {

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Request method for WooCommerce REST API endpoints
   * Used for: fetching orders, order details (uses Basic Auth via proxy)
   */
  private async wcRequest<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const headers: any = {
        ...getDefaultHeaders(),
        ...options.headers,
      };

      // For WooCommerce endpoints, proxy will add Basic Auth
      // No need to send JWT token here
      
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (IS_DEV) {
          console.error('wcRequest error:', {
            url,
            status: response.status,
            errorData
          });
        }
        return {
          success: false,
          message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
          data: errorData
        };
      }

      const data = await response.json();

      return {
        success: true,
        data,
        message: data.message,
        language: this.language,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      if (IS_DEV) {
        console.error('wcRequest exception:', error);
      }
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Ensure we have backend-provided status labels cached for the current locale
   */
  private async ensureStatusLabels(locale: string): Promise<void> {
    if (this.statusLabelsLocale === locale && Object.keys(this.statusLabels).length > 0) {
      return;
    }

    if (this.statusLabelsPromise) {
      await this.statusLabelsPromise;
      return;
    }

    const fetchPromise = (async () => {
      try {
        const url = buildWCUrl('/boat-booking/v1/order-status-labels', { lang: locale });
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            ...getDefaultHeaders(),
          },
        });

        if (!response.ok) {
          if (IS_DEV) {
            console.warn('Failed to fetch status labels', response.status, response.statusText);
          }
          return;
        }

        const data = await response.json().catch(() => null);
        const labelsPayload = data?.labels || {};
        const normalized: Record<string, string> = {};

        Object.entries(labelsPayload).forEach(([key, value]) => {
          if (typeof value === 'string') {
            const wcKey = key.startsWith('wc-') ? key : `wc-${key}`;
            normalized[wcKey] = value;
            normalized[wcKey.replace('wc-', '')] = value;
          }
        });

        if (Object.keys(normalized).length > 0) {
          this.statusLabels = normalized;
          this.statusLabelsLocale = locale;
        }
      } catch (error) {
        if (IS_DEV) {
          console.warn('Error fetching status labels:', error);
        }
      } finally {
        this.statusLabelsPromise = null;
      }
    })();

    this.statusLabelsPromise = fetchPromise;
    await fetchPromise;
  }

  private applyStatusLabel(order: any): void {
    if (!order) {
      return;
    }

    if (order.order_status_name) {
      return;
    }

    const status = (order.order_status || order.status || '').toLowerCase();
    if (!status) {
      return;
    }

    const slug = status.startsWith('wc-') ? status : `wc-${status}`;
    const label = this.statusLabels[slug] || this.statusLabels[slug.replace('wc-', '')];
    if (label) {
      order.order_status_name = label;
    }
  }

  /**
   * Public helper so other services can reuse cached status labels
   */
  public getStatusLabel(status: string): string | undefined {
    if (!status) {
      return undefined;
    }

    const slug = status.startsWith('wc-') ? status : `wc-${status}`;
    return this.statusLabels[slug] || this.statusLabels[slug.replace('wc-', '')];
  }

  /**
   * Allow other consumers to ensure labels are loaded for a locale
   */
  public async ensureStatusLabelsLoaded(locale?: string): Promise<void> {
    await this.ensureStatusLabels(locale || this.language);
  }

  /**
   * Calculate pricing for a booking
   */
  async calculatePrice(
    productId: number,
    adults: number,
    children: number,
    infants: number,
    addons: { id: number; qty: number }[] = []
  ): Promise<APIResponse<{ data: PriceCalculation }>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_CALCULATE);

const response = await this.request(url, {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        adults,
        children,
        infants,
        addons
      })
    });

return response;
  }

  /**
   * Create a temporary booking hold
   */
  async createBookingHold(
    productId: number,
    date: string,
    time: string,
    adults: number,
    children: number,
    infants: number
  ): Promise<APIResponse<BookingHold>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_HOLD);

    return this.request<BookingHold>(url, {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        date,
        time,
        adults,
        children,
        infants
      })
    });
  }

  /**
   * Update an existing hold passenger count
   */
  async updateBookingHold(
    holdId: string,
    passengers: number
  ): Promise<APIResponse<BookingHold>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.BOAT_HOLD}/${holdId}`);
    return this.request<BookingHold>(url, {
      method: 'PATCH',
      body: JSON.stringify({ passengers })
    });
  }

  /**
   * Create final booking order using unified endpoint
   */
  async createBookingOrder(
    orderData: CreateBookingOrderData
  ): Promise<APIResponse<BookingOrderResponse>> {
    try {
      // Use unified endpoint for ALL payment methods
      // User authentication via JWT token in Authorization header (added by request method)
      const url = buildWCUrl('/boat-order');
      const response = await this.request<BookingOrderResponse>(url, {
        method: 'POST',
        body: JSON.stringify({
          product_id: orderData.productId,
          date: orderData.date,
          time: orderData.time,
          adults: orderData.adults,
          children: orderData.children,
          infants: orderData.infants,
          addons: orderData.addons,
          passengers: orderData.passengers,
          contact_name: orderData.contactName,
          contact_email: orderData.contactEmail,
          contact_phone: orderData.contactPhone,
          hold_id: orderData.holdId,
          coupon_code: orderData.couponCode,
          payment_method: orderData.paymentMethod || 'cod',
          special_requests: orderData.specialRequests
        })
      });
      
      return response;
    } catch (error: any) {
      // ✅ Return REAL error from backend instead of generic message
      const errorMessage = error?.message || error?.error || error?.technical_error;
      
      return {
        success: false,
        message: errorMessage || (this.language === 'ar' 
          ? 'فشل في إنشاء الطلب. يرجى المحاولة مرة أخرى.' 
          : 'Failed to create order. Please try again.'),
        error: errorMessage, // ✅ Pass real error
        technical_error: error?.technical_error, // ✅ Pass technical details
      };
    }
  }

  /**
   * Validate coupon code before applying
   * Uses WooCommerce REST API: /wp-json/wc/v3/coupons?code=COUPON_CODE
   */
  async validateCoupon(
    couponCode: string,
    productId?: number,
    totalAmount?: number
  ): Promise<APIResponse<{
    valid: boolean;
    coupon?: {
      id: number;
      code: string;
      amount: string;
      discount_type: 'percent' | 'fixed_cart' | 'fixed_product';
      description: string;
      date_expires: string | null;
      minimum_amount: string;
      maximum_amount: string;
      product_ids: number[];
      excluded_product_ids: number[];
      usage_limit: number | null;
      usage_count: number;
    };
    discount_amount?: number;
    error?: string;
  }>> {
    try {
      // Use WooCommerce REST API to get coupon details
      const url = buildWCUrl('/coupons', { code: couponCode });
      
      const response = await this.wcRequest<any[]>(url);
      
      if (!response.success || !response.data || response.data.length === 0) {
        return {
          success: true,
          data: {
            valid: false,
            error: 'Coupon not found'
          }
        };
      }
      
      const coupon = response.data[0];
      
      // Check if coupon is valid
      const now = new Date();
      const expiryDate = coupon.date_expires ? new Date(coupon.date_expires) : null;
      
      if (expiryDate && expiryDate < now) {
        return {
          success: true,
          data: {
            valid: false,
            error: 'Coupon has expired'
          }
        };
      }
      
      // Check usage limit
      if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
        return {
          success: true,
          data: {
            valid: false,
            error: 'Coupon usage limit reached'
          }
        };
      }
      
      // Check minimum amount
      if (totalAmount && coupon.minimum_amount && parseFloat(coupon.minimum_amount) > totalAmount) {
        return {
          success: true,
          data: {
            valid: false,
            error: `Minimum order amount is ${coupon.minimum_amount}`
          }
        };
      }
      
      // Check maximum amount (0.00 or empty means no maximum limit in WooCommerce)
      if (totalAmount && coupon.maximum_amount && parseFloat(coupon.maximum_amount) > 0 && parseFloat(coupon.maximum_amount) < totalAmount) {
        return {
          success: true,
          data: {
            valid: false,
            error: `Maximum order amount is ${coupon.maximum_amount}`
          }
        };
      }
      
      // Check product restrictions
      if (productId && coupon.product_ids && coupon.product_ids.length > 0) {
        if (!coupon.product_ids.includes(productId)) {
          return {
            success: true,
            data: {
              valid: false,
              error: 'Coupon not applicable to this product'
            }
          };
        }
      }
      
      // Check excluded products
      if (productId && coupon.excluded_product_ids && coupon.excluded_product_ids.includes(productId)) {
        return {
          success: true,
          data: {
            valid: false,
            error: 'Coupon not applicable to this product'
          }
        };
      }
      
      // Calculate discount amount
      let discountAmount = 0;
      if (totalAmount) {
        if (coupon.discount_type === 'percent') {
          discountAmount = (totalAmount * parseFloat(coupon.amount)) / 100;
        } else if (coupon.discount_type === 'fixed_cart' || coupon.discount_type === 'fixed_product') {
          discountAmount = parseFloat(coupon.amount);
        }
      }
      
      return {
        success: true,
        data: {
          valid: true,
          coupon: coupon,
          discount_amount: discountAmount
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to validate coupon',
        data: {
          valid: false,
          error: error.message || 'Failed to validate coupon'
        }
      };
    }
  }

  /**
   * Apply coupon code
   */
  async applyCoupon(
    couponCode: string,
    totalAmount: number
  ): Promise<APIResponse<CouponResponse>> {
    const url = buildWCUrl(API_CONFIG.ENDPOINTS.BOAT_COUPON);

return this.request(url, {
      method: 'POST',
      body: JSON.stringify({
        coupon_code: couponCode,
        total_amount: totalAmount
      })
    });
  }

  /**
   * Cancel booking hold
   */
  async cancelBookingHold(holdId: string): Promise<APIResponse<{ message: string }>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.BOAT_HOLD}/${holdId}`);

return this.request(url, {
      method: 'DELETE'
    });
  }

  /**
   * Get booking details by order ID
   */
  async getBookingDetails(orderId: number): Promise<APIResponse<BookingOrderResponse>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}`, { lang: this.language });

// Use wcRequest for WooCommerce API endpoints
    const response = await this.wcRequest(url);
    if (response?.success && response?.data) {
      await this.ensureStatusLabels(this.language);
      this.applyStatusLabel(response.data);
    }
    return response;
  }

  /**
   * Get user's booking history - with pagination (10 per page)
   */
  async getUserBookings(page: number = 1, perPage: number = 10): Promise<APIResponse<{
    bookings: BookingOrderResponse[];
    total: number;
    pages: number;
  }>> {
    // Get current user ID
    const user = useAuthStore.getState().user;
    if (!user || !user.id) {
      return {
        success: false,
        message: 'User not authenticated',
      };
    }

    const url = buildWCUrl(API_CONFIG.ENDPOINTS.ORDERS, {
      customer: user.id.toString(),
      page,
      per_page: perPage,
      orderby: 'date',
      order: 'desc',
      lang: this.language
    });

    // Use wcRequest for WooCommerce API endpoints (uses Basic Auth)
    const response = await this.wcRequest<BookingOrderResponse[]>(url);

    // WooCommerce returns array directly, wrap it in expected structure
    if (response.success && Array.isArray(response.data)) {
      await this.ensureStatusLabels(this.language);
      response.data.forEach(order => this.applyStatusLabel(order));
      const ordersCount = response.data.length;

      // Check if there might be more pages
      const hasMore = ordersCount === perPage;
      
      return {
        success: true,
        data: {
          bookings: response.data,
          total: ordersCount,
          pages: hasMore ? page + 1 : page
        },
        message: `Found ${ordersCount} orders`
      };
    }

    return response as any;
  }

  /**
   * Update booking status
   */
  async updateBookingStatus(
    orderId: number, 
    status: 'pending' | 'processing' | 'completed' | 'cancelled'
  ): Promise<APIResponse<BookingOrderResponse>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}`);

// Use wcRequest for WooCommerce API endpoints
    return this.wcRequest(url, {
      method: 'PUT',
      body: JSON.stringify({
        status
      })
    });
  }

  /**
   * Cancel booking
   */
  async cancelBooking(orderId: number, reason?: string): Promise<APIResponse<{ message: string }>> {
    const url = buildWCUrl(`${API_CONFIG.ENDPOINTS.ORDERS}/${orderId}`);

// Use wcRequest for WooCommerce API endpoints
    return this.wcRequest(url, {
      method: 'PUT',
      body: JSON.stringify({
        status: 'cancelled',
        cancel_reason: reason
      })
    });
  }

  /**
   * Get ticket URL for a booking
   * Searches through order meta_data for ticket URL
   */
  async getTicketUrl(orderId: number): Promise<APIResponse<{ ticket_url: string | null }>> {
    try {
      const detailsResponse = await this.getBookingDetails(orderId);
      
      if (!detailsResponse.success || !detailsResponse.data) {
        return {
          success: false,
          message: 'Could not fetch order details',
          data: null as any,
        };
      }

      // Search for ticket URL in order data
      const orderData = detailsResponse.data as any;
      let ticketUrl: string | null = null;

      // Check meta_data array
      if (orderData.meta_data && Array.isArray(orderData.meta_data)) {
        const ticketMeta = orderData.meta_data.find((m: any) => 
          m.key === 'sbb_ticket_pdf_url' || 
          m.key === '_ticket_url' ||
          m.key === 'ticket_url'
        );
        if (ticketMeta?.value) {
          ticketUrl = ticketMeta.value;
        }
      }

      // Check direct properties
      if (!ticketUrl && orderData.ticket_url) {
        ticketUrl = orderData.ticket_url;
      }

      if (!ticketUrl) {
        return {
          success: false,
          message: 'Ticket not generated yet',
          data: { ticket_url: null },
        };
      }

      return {
        success: true,
        data: { ticket_url: ticketUrl },
        message: 'Ticket URL retrieved',
      };
    } catch (error) {

      return {
        success: false,
        message: 'Failed to get ticket URL',
        data: { ticket_url: null },
      };
    }
  }

  /**
   * Download ticket PDF
   * Opens ticket in new window/tab
   */
  downloadTicket(ticketUrl: string): void {
    window.open(ticketUrl, '_blank');

  }

  /**
   * Get user booking statistics - fetches ALL bookings for accurate stats
   */
  async getBookingStats(): Promise<APIResponse<{
    total_bookings: number;
    completed_bookings: number;
    pending_bookings: number;
    cancelled_bookings: number;
    total_spent: number;
    currency: string;
  }>> {
    try {
      // Fetch ALL user orders (increase limit to get all)
      let allOrders: any[] = [];
      let page = 1;
      let hasMore = true;
      
      // Fetch all pages
      while (hasMore && page <= 10) { // Max 10 pages = 1000 orders
        const ordersResponse = await this.getUserBookings(page, 100);
        
        if (!ordersResponse.success || !ordersResponse.data) {
          break;
        }

        const pageOrders = ordersResponse.data.bookings || [];
        allOrders = [...allOrders, ...pageOrders];
        
        // Check if there are more pages
        hasMore = pageOrders.length === 100;
        page++;
      }

const stats = {
        total_bookings: allOrders.length,
        completed_bookings: allOrders.filter((o: any) => o.status === 'completed' || o.order_status === 'completed').length,
        pending_bookings: allOrders.filter((o: any) => {
          const status = o.status || o.order_status;
          return ['processing', 'pending', 'on-hold'].includes(status);
        }).length,
        cancelled_bookings: allOrders.filter((o: any) => {
          const status = o.status || o.order_status;
          return ['cancelled', 'failed', 'refunded'].includes(status);
        }).length,
        total_spent: allOrders
          .filter((o: any) => {
            const status = o.status || o.order_status;
            return ['completed', 'processing'].includes(status);
          })
          .reduce((sum: number, o: any) => sum + parseFloat(o.total || '0'), 0),
        currency: 'SAR', // Default currency
      };

return {
        success: true,
        data: stats,
        message: 'Stats calculated',
      };
    } catch (error) {

      return {
        success: false,
        message: 'Failed to calculate stats',
        data: null as any,
      };
    }
  }
}

// Create singleton instance
const bookingService = new BookingService();

export default bookingService;
